import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Text, } from '@tremor/react';
import * as Sentry from '@sentry/react';
import SummaryCard from '../../../../components/shared/Charts/SummaryCard/SummaryCard';
import { ServiceSignupFunnel, SignupFunnelData, FunnelPeriod } from '../../../../services/service-signup-funnel';
import { UNIT } from '../../helper';
import { LineChart } from '../../../../components/shared/Charts/LineChart/LineChart';

const SUBSCRIPTION_STEPS = new Set(['subscription_prio_activated', 'subscription_marketplace_activated']);

interface FunnelRow {
  step: string;
  label: string;
  value: number;
  count: number;
  uniqueSessions: number;
  isSubscription: boolean;
}

interface RowProps {
  row: FunnelRow;
  prevValue: number | null;
  baselineValue: number;
  description?: string;
}

/** Single funnel row — label, bar scaled to baseline with count inside, conversion pill. */
const FunnelBarRow: FC<RowProps> = ({ row, prevValue, baselineValue, description }) => {
  const barWidth = baselineValue > 0 ? (row.value / baselineValue) * 100 : 0;
  const conversionPct = prevValue && prevValue > 0
    ? Math.round((row.value / prevValue) * 1000) / 10
    : null;
  const clampedBarWidth = Math.max(barWidth, 0.5);

  return (
    <div className="flex items-center gap-3" style={{ padding: '4px 0' }}>
      <div
        className="text-sm flex items-center"
        style={{ width: '140px', flexShrink: 0, color: 'var(--gray-700)', fontWeight: 500 }}
      >
        <span title={description || ''} style={{ cursor: description ? 'help' : 'default' }}>
          {row.label}
        </span>
      </div>
      <div
        className="flex-1 relative rounded-md overflow-hidden"
        style={{ height: '32px', background: 'var(--gray-50)' }}
      >
        {row.value > 0 && (
          <div
            style={{
              width: `${clampedBarWidth}%`,
              height: '100%',
              background: 'var(--orange-500)',
              transition: 'width 400ms ease-out',
            }}
          />
        )}
        {/* Dark number — visible where bar doesn't reach */}
        <div
          className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-semibold"
          style={{
            color: 'var(--gray-900)',
            fontVariantNumeric: 'tabular-nums',
            clipPath: `inset(0 0 0 ${clampedBarWidth}%)`,
          }}
        >
          {row.value.toLocaleString('sv-SE')}
        </div>
        {/* White number — visible where bar reaches */}
        <div
          className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-semibold"
          style={{
            color: '#fff',
            fontVariantNumeric: 'tabular-nums',
            clipPath: `inset(0 ${100 - clampedBarWidth}% 0 0)`,
          }}
        >
          {row.value.toLocaleString('sv-SE')}
        </div>
      </div>
      <div
        className="text-xs flex-shrink-0 text-center"
        style={{
          width: '64px',
          fontVariantNumeric: 'tabular-nums',
          color: conversionPct === null ? 'transparent' : (conversionPct < 50 ? 'var(--red-700)' : 'var(--gray-600)'),
          fontWeight: 500,
        }}
      >
        {conversionPct !== null ? `${conversionPct}%` : ''}
      </div>
    </div>
  );
};

const SignupFunnelSection: FC = () => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'signupFunnel' });
  const [data, setData] = useState<SignupFunnelData | null>(null);
  const [period, setPeriod] = useState<FunnelPeriod>('all');
  const [loading, setLoading] = useState(true);

  const periodOptions: { label: string; value: FunnelPeriod }[] = [
    { label: t('period.all', 'Alla'), value: 'all' },
    { label: t('period.year', 'År'), value: 'year' },
    { label: t('period.month', 'Månad'), value: 'month' },
    { label: t('period.week', 'Vecka'), value: 'week' },
  ];

  const stepDescriptions: Record<string, string> = {
    signup_wizard_started: t('stepDesc.started', 'Antal unika sessioner som öppnat /supplier-sign-up. Mäts med en unik sessionId per wizard-start.'),
    signup_wizard_step_company: t('stepDesc.company', 'Unika sessioner som slutfört steg 1 (företagsuppgifter).'),
    signup_wizard_step_categories: t('stepDesc.categories', 'Unika sessioner som valt minst en yrkeskategori.'),
    signup_wizard_step_places: t('stepDesc.places', 'Unika sessioner som valt minst ett verksamhetsområde.'),
    signup_wizard_step_about_you: t('stepDesc.aboutYou', 'Unika sessioner som fyllt i sina personuppgifter.'),
    signup_wizard_step_invite: t('stepDesc.invite', 'Unika sessioner som passerat kollega-steget (inbjudna eller hoppat över).'),
    signup_wizard_completed: t('stepDesc.completed', 'Unika konton som verifierats och skapats i databasen.'),
    subscription_activated: t('stepDesc.subscriptionActivated', 'Totalt antal nya Prio- + Marknadsplatsen-prenumerationer aktiverade. Räknar bara nya ordrar (inte uncancel eller renewals).'),
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ServiceSignupFunnel.getFunnelData(period)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => {
        console.error('Failed to load signup funnel data', err);
        Sentry.captureException(err);
        if (!cancelled) setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const { rows, started, completed, prio, marketplace, conversionRate } = useMemo(() => {
    if (!data || !Array.isArray(data.funnel)) {
      return { rows: [] as FunnelRow[], started: null, completed: null, prio: null, marketplace: null, conversionRate: 0 };
    }
    const wizardRows: FunnelRow[] = data.funnel
      .filter(s => !SUBSCRIPTION_STEPS.has(s.step))
      .map(s => ({ ...s, value: s.uniqueSessions, isSubscription: false }));

    const prioSrc = data.funnel.find(s => s.step === 'subscription_prio_activated');
    const marketplaceSrc = data.funnel.find(s => s.step === 'subscription_marketplace_activated');

    // Combined activation step: Prio + Marknadsplatsen (parallel outcomes, not sequential)
    const activatedRow: FunnelRow = {
      step: 'subscription_activated',
      label: t('subscriptionActivated', 'Aktiverat abonnemang'),
      value: (prioSrc?.count || 0) + (marketplaceSrc?.count || 0),
      count: (prioSrc?.count || 0) + (marketplaceSrc?.count || 0),
      uniqueSessions: 0,
      isSubscription: true,
    };

    const mapped = [...wizardRows, activatedRow];
    const startedRow = mapped.find(r => r.step === 'signup_wizard_started') || null;
    const completedRow = mapped.find(r => r.step === 'signup_wizard_completed') || null;
    const prioRow = prioSrc ? { ...prioSrc, value: prioSrc.count, isSubscription: true } : null;
    const marketplaceRow = marketplaceSrc ? { ...marketplaceSrc, value: marketplaceSrc.count, isSubscription: true } : null;
    const convRate = startedRow && startedRow.value > 0
      ? Math.round(((completedRow?.value || 0) / startedRow.value) * 1000) / 10
      : 0;
    return {
      rows: mapped,
      started: startedRow,
      completed: completedRow,
      prio: prioRow,
      marketplace: marketplaceRow,
      conversionRate: convRate,
    };
  }, [data]);

  if (!data || !Array.isArray(data.funnel) || !Array.isArray(data.trend)) return null;

  const baseline = rows[0]?.value || 0;

  const labelStarted = t('trend.started', 'Startade');
  const labelCompleted = t('trend.completed', 'Slutförda');

  const trendChartData = data.trend.map(entry => ({
    period: entry.period,
    [labelStarted]: entry.started,
    [labelCompleted]: entry.completed,
  }));

  return (
    <div className="flex flex-col gap-4 mt-6 mb-6">
      <div className="flex items-center justify-between">
        <Text className="font-semibold text-lg">{t('title', 'Leverantör signup-funnel')}</Text>
        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
          {periodOptions.map((opt, i) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPeriod(opt.value)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRight: i < periodOptions.length - 1 ? '1px solid var(--gray-300)' : 'none',
                fontSize: 14,
                fontWeight: 400,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                background: period === opt.value ? 'var(--orange-500)' : 'var(--white)',
                color: period === opt.value ? 'var(--white)' : 'var(--gray-700)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4" style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 200ms ease' }}>
        <div className="grid grid-cols-4 gap-4">
          <SummaryCard
            title={t('cards.signups.title', 'Signups')}
            value={completed?.value || 0}
            infoText={t('cards.signups.info', 'Unika konton skapade via signup-wizarden under perioden. Räknas när email-verifieringen lyckas och Organization + User sparats i databasen.')}
          />
          <SummaryCard
            title={t('cards.conversion.title', 'Konvertering')}
            value={conversionRate}
            unit={UNIT.PERCENTAGE}
            infoText={t('cards.conversion.info', 'Andelen unika sessioner som går från Landningssida till Konto skapat. Formel: slutförda sessioner ÷ startade sessioner × 100.')}
          />
          <SummaryCard
            title={t('cards.prio.title', 'Prio')}
            value={prio?.value || 0}
            infoText={t('cards.prio.info', 'Nya Accurator Prio-prenumerationer aktiverade under perioden. Räknar endast nya ordrar, inte uncancel eller förnyelser.')}
          />
          <SummaryCard
            title={t('cards.marketplace.title', 'Marknadsplatsen')}
            value={marketplace?.value || 0}
            infoText={t('cards.marketplace.info', 'Nya Marknadsplatsen-prenumerationer aktiverade under perioden. Räknar endast nya ordrar, inte uncancel eller förnyelser.')}
          />
        </div>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <Text className="font-semibold">{t('funnel.title', 'Funnel')}</Text>
            <div className="flex gap-4 text-xs" style={{ color: 'var(--gray-500)' }}>
              <span>{t('funnel.count', 'Antal')}</span>
              <span>{t('funnel.pctFromPrev', '% från föregående')}</span>
            </div>
          </div>

          <div className="flex flex-col">
            {rows.map((row, i) => (
              <FunnelBarRow
                key={row.step}
                row={row}
                prevValue={i > 0 ? rows[i - 1].value : null}
                baselineValue={baseline}
                description={stepDescriptions[row.step]}
              />
            ))}
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <LineChart
            title={t('trend.title', 'Trend')}
            data={trendChartData}
            index="period"
            categories={[labelStarted, labelCompleted]}
            colors={['gray', 'orange']}
          />
        </div>
      </div>
    </div>
  );
};

export default SignupFunnelSection;
